import { ApiService, ResponseData } from './api'

export interface ConnChannelData {
	id?: number
	space_id?: number
	title: string
	description?: string | null
	channel_kind_key?: string
	provider_key?: string
	module_key?: string
	outbound_template_json?: string | null
	endpoint_key?: string | null
	inbound_config_json?: string | null
	secret_config_json?: string | null
	has_secret_config?: boolean | number | string
	status_key?: string
	runtime_sync_status_key?: 'pending' | 'synced' | 'failed' | string
	runtime_synced_at?: string | null
	runtime_sync_error_text?: string | null
	publish?: {
		success?: boolean
		job_id?: number
		status_key?: string
		validation_error_count?: number
		validation_warning_count?: number
		runtime_sync_status?: string
		row_hash?: string
		error?: string
		message?: string
	}
	created_at?: string
	updated_at?: string
}
export interface ConnChannelUpdateData {
	title?: string
	description?: string | null
	channel_kind_key?: string
	provider_key?: string
	module_key?: string
	outbound_template_json?: string | null
	endpoint_key?: string | null
	inbound_config_json?: string | null
	secret_config_json?: string | null
	status_key?: string
}

export interface ConnChannelExchangeData {
	id: number
	space_id: number
	channel_id: number
	direction_key: string
	status_key: string
	request_json?: string | null
	response_json?: string | null
	preview_text?: string | null
	error_text?: string | null
	created_at?: string
	storage_key?: 'runtime' | 'control_plane' | string
	runtime_event?: ConnRuntimeEventLogData | null
}

export interface ConnRuntimeEventLogData {
	id: number
	space_id?: number
	source_instrument_key?: string
	source_entity_type?: string | null
	source_entity_id?: string | null
	module_key?: string
	event_type_key?: string
	status_key?: string
	source?: Record<string, unknown> | null
	payload?: Record<string, unknown> | null
	event?: Record<string, unknown> | null
	routing?: Record<string, unknown> | null
	created_at?: string | null
}

export interface ConnSendRequestPayload {
	method?: string
	url?: string
	headers?: Record<string, unknown>
	query?: Record<string, unknown>
	body?: unknown
	timeout_ms?: number | null
	execute_now?: boolean
}

export interface ConnWebhookSubscriptionPayload {
	webhook_url?: string
	update_types?: string[]
}

export interface ConnFirstMaxChatbotSetupPayload {
	mode?: 'prepare' | 'execute' | string
	reply_text?: string
	title?: string
	description?: string | null
	webhook_url?: string
	webhook_base_url?: string
	token_env?: string
	dry_run?: boolean
}

export interface ConnPreparedRequestData {
	channel_id: number
	channel_kind_key: string
	provider_key: string
	module_key: string
	method: string
	url: string
	final_url: string
	headers: Record<string, unknown>
	query: Record<string, unknown>
	body: unknown
	timeout_ms?: number | null
	prepared_at: string
}

export interface ConnSendRequestResult {
	channel: {
		id: number
		title: string
		provider_key?: string | null
		module_key?: string | null
	}
	exchange: {
		id: number
		space_id?: number
		channel_id: number
		direction_key: string
		status_key: string
		preview_text?: string | null
		request_json?: string | null
		response_json?: string | null
		error_text?: string | null
		storage_key?: 'runtime' | 'control_plane' | string
	}
	request: ConnPreparedRequestData
	runtime?: {
		requested: boolean
		executed: boolean
		success?: boolean | null
		status_key?: string | null
		preview_text?: string | null
		error_text?: string | null
		response?: Record<string, unknown> | null
		request_url?: string | null
		storage_key?: 'runtime' | 'control_plane' | string | null
	}
}

export interface ConnWebhookSubscriptionResult {
	channel: {
		id: number
		title: string
		provider_key?: string | null
		module_key?: string | null
	}
	webhook_url: string
	update_types: string[]
	subscription?: Record<string, unknown>
	exchange: ConnSendRequestResult['exchange']
	delete_exchange?: ConnSendRequestResult['exchange']
	request: ConnPreparedRequestData
	delete_request?: ConnPreparedRequestData
	runtime?: ConnSendRequestResult['runtime']
	delete_runtime?: ConnSendRequestResult['runtime']
	message?: string
}

export interface ConnFirstMaxChatbotSetupResult {
	control_plane?: Record<string, unknown>
	runtime?: Record<string, unknown>
	runtime_channel_sync?: Record<string, unknown> | null
	channel?: ConnChannelData
	publish?: {
		success?: boolean
		job_id?: number
		status_key?: string
		validation_error_count?: number
		validation_warning_count?: number
		entities_count?: number
		transitions_count?: number
		verified_entities_count?: number
		verified_transitions_count?: number
		cleanup_status?: string
	}
	message?: string
}

export interface ConnFirstMaxChatbotStatusCheck {
	key: string
	title: string
	status: 'ok' | 'warn' | 'missing' | string
	detail?: string | null
}

export interface ConnFirstMaxChatbotStatusResult {
	channel?: {
		id: number
		title: string
		endpoint_key?: string | null
		has_secret_config?: boolean
		has_token_config?: boolean
		has_webhook_secret?: boolean
		runtime_sync_status_key?: string | null
		runtime_sync_error_text?: string | null
	}
	runtime_mode?: 'preview' | 'prepare' | 'execute' | string
	webhook_url?: string | null
	route?: Record<string, unknown>
	control_plane_route?: Record<string, unknown>
	subscription?: Record<string, unknown>
	checks?: ConnFirstMaxChatbotStatusCheck[]
	ready_for_synthetic?: boolean
	ready_for_live?: boolean
	next_action?: {
		key?: string
		title?: string
		detail?: string
	}
}

export interface ConnInboundTestPayload {
	body?: unknown
	payload?: unknown
	target_execution_mode?: 'preview' | 'prepare' | 'execute' | string
}

export interface ConnInboundTestResult {
	endpoint_key?: string
	request_url?: string
	runtime?: Record<string, unknown>
}

export interface ConnEventCondition {
	id?: number
	condition_type: string
	condition_field: string
	condition_value?: unknown
	sort_order?: number
	is_active?: boolean
}

export interface ConnEventOutcome {
	outcome_type: 'none' | 'operation' | 'target' | string
	outcome_key?: string | null
	outcome_data?: Record<string, unknown>
}

export interface ConnEventRule {
	id: number
	handler_id?: number
	space_id?: number
	condition_join: 'all' | 'any' | string
	outcome_type: 'none' | 'operation' | 'target' | string
	outcome_key?: string | null
	outcome_data?: Record<string, unknown>
	sort_order: number
	is_active: boolean
	conditions: ConnEventCondition[]
}

export interface ConnEventRouteTarget {
	id: number
	target_instrument_key: string
	target_entity_type: string
	target_entity_id: string
	target_action_key?: string | null
	target_module_key?: string | null
	context?: {
		instrument_key?: string | null
		entity_type?: string | null
		entity_id?: string | null
	}
	data_map?: Record<string, unknown>
	sort_order: number
	is_active: boolean
}

export interface ConnEventRouteDescriptors {
	event_types?: Array<{
		type: string
		title: string
		filter_fields?: string[]
	}>
	condition_types?: Array<{
		value: string
		title: string
		expects_value?: boolean
	}>
	condition_presets?: Array<{
		key: string
		title: string
		condition_type: string
		condition_field: string
		condition_value?: unknown
		event_types?: string[]
		scopes?: string[]
	}>
	condition_groups?: Array<{
		key: string
		title: string
		condition_join?: 'all' | 'any' | string
		conditions: Array<{
			condition_type: string
			condition_field: string
			condition_value?: unknown
		}>
		event_types?: string[]
		scopes?: string[]
	}>
	match_policies?: Array<{
		value: string
		title: string
	}>
	condition_joins?: Array<{
		value: string
		title: string
	}>
	outcomes?: Array<ConnEventOutcome & {
		title?: string
		operation_type?: string
		module_key?: string
		schema?: Record<string, unknown>
	}>
}

export interface ConnEventRoute {
	handler: {
		id: number
		space_id: number
		module_key: string
		event_type_key: string
		title: string
		description?: string | null
		filter?: Record<string, unknown>
		response_policy?: Record<string, unknown>
		match_policy?: string
		accept_join?: string
		on_reject?: ConnEventOutcome
		on_no_match?: ConnEventOutcome
		accept_conditions?: ConnEventCondition[]
		rules?: ConnEventRule[]
		priority: number
		is_active: boolean
	}
	binding: {
		id: number
		owner_instrument_key: string
		owner_entity_type: string
		owner_entity_id: string
		module_key: string
		context?: {
			instrument_key?: string | null
			entity_type?: string | null
			entity_id?: string | null
		}
		response_policy?: Record<string, unknown>
		priority: number
		is_active: boolean
	}
	target?: ConnEventRouteTarget | null
	targets?: ConnEventRouteTarget[]
}

export interface ConnEventRoutePayload {
	title?: string | null
	description?: string | null
	module_key: string
	event_type_key: string
	filter_json?: Record<string, unknown> | string | null
	response_policy_json?: Record<string, unknown> | string | null
	match_policy?: 'first_match' | 'all_matches' | string
	accept_join?: 'all' | 'any' | string
	condition_join?: 'all' | 'any' | string
	accept_conditions?: Array<Omit<ConnEventCondition, 'id' | 'is_active'>>
	rule_conditions?: Array<Omit<ConnEventCondition, 'id' | 'is_active'>>
	on_reject?: ConnEventOutcome | null
	on_no_match?: ConnEventOutcome | null
	priority?: number | string | null
	target_entity_type: 'place' | 'step' | 'operation'
	target_entity_id: string
	data_map_json?: Record<string, unknown> | string | null
	sort_order?: number | string | null
}

export type ConnEventRouteActionPayload = {
	action: 'enable' | 'disable' | 'delete'
	handler_id: number | string
	binding_id: number | string
} | {
	action: 'enable_rule' | 'disable_rule' | 'delete_rule'
	handler_id: number | string
	binding_id: number | string
	rule_id: number | string
} | (ConnEventRoutePayload & {
	action: 'replace' | 'update'
	handler_id: number | string
	binding_id: number | string
}) | (ConnEventRoutePayload & {
	action: 'replace_rule'
	handler_id: number | string
	binding_id: number | string
	rule_id: number | string
})

export class ConnService {

	private readonly apiService: ApiService
	private readonly cpDomain: string

	constructor(apiService: ApiService)
	{
		if (!apiService)
			throw new Error('ApiService не инициализирован')
		if (!apiService.getConfig()?.cp_domain)
			throw new Error('Отсутствует CP-домен в конфигурации сервера')

		this.apiService = apiService
		this.cpDomain = apiService.getConfig().cp_domain
	}

	async getChannels(spaceId: string | number): Promise<ResponseData>
	{
		return this.fetchConn(spaceId, '/channels', 'GET')
	}

	async createChannel(
		spaceId: string | number,
		channelData: ConnChannelUpdateData & Pick<ConnChannelData, 'title'>
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, '/channels', 'POST', channelData)
	}

	async getChannel(spaceId: string | number, channelId: string | number): Promise<ResponseData>
	{
		return this.fetchConn(spaceId, `/channel/${channelId}`, 'GET')
	}

	async updateChannel(
		spaceId: string | number,
		channelId: string | number,
		channelData: ConnChannelUpdateData
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel/${channelId}`, 'PATCH', channelData)
	}

	async deleteChannel(spaceId: string | number, channelId: string | number): Promise<ResponseData>
	{
		return this.fetchConn(spaceId, `/channel/${channelId}`, 'DELETE')
	}

	async getChannelExchanges(spaceId: string | number, channelId: string | number): Promise<ResponseData>
	{
		return this.fetchConn(spaceId, `/channel-exchanges/${channelId}`, 'GET')
	}

	async sendChannelRequest(
		spaceId: string | number,
		channelId: string | number,
		payload: ConnSendRequestPayload
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-send/${channelId}`, 'POST', payload)
	}

	async registerMaxWebhookSubscription(
		spaceId: string | number,
		channelId: string | number,
		payload: ConnWebhookSubscriptionPayload = {}
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-webhook-subscription/${channelId}`, 'POST', payload)
	}

	async checkMaxWebhookSubscription(
		spaceId: string | number,
		channelId: string | number
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-webhook-subscription/${channelId}`, 'GET')
	}

	async deleteMaxWebhookSubscription(
		spaceId: string | number,
		channelId: string | number
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-webhook-subscription/${channelId}`, 'DELETE')
	}

	async setupFirstMaxChatbot(
		spaceId: string | number,
		channelId: string | number,
		payload: ConnFirstMaxChatbotSetupPayload
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-first-max-chatbot/${channelId}`, 'POST', payload)
	}

	async getFirstMaxChatbotStatus(
		spaceId: string | number,
		channelId: string | number
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-first-max-chatbot-status/${channelId}`, 'GET')
	}

	async testChannelInbound(
		spaceId: string | number,
		channelId: string | number,
		payload: ConnInboundTestPayload
	): Promise<ResponseData> {
		return this.fetchConn(spaceId, `/channel-inbound-test/${channelId}`, 'POST', payload)
	}

	private async fetchConn(
		spaceId: string | number,
		path: string,
		method: string,
		body: unknown = undefined
	): Promise<ResponseData> {
		try {
			const response = await this.apiService.doFetch(
				this.cpDomain,
				`/${spaceId}/conn/s${path}`,
				method,
				body
			)

			return await this.apiService.getResponseJson(response)
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка запроса Connector API'
			}
		}
	}
}
